/* ==========================================================================
   Beanwatch · Animationsbasis
   Läuft auf jeder Seite und deckt alles ab, was überall gleich ist:
   Header, Fortschritt, Überschriften, Reveals, Sonne, Wellen, Laufband,
   Bilder-Parallax und magnetische Buttons.

   Seitenspezifisches steht in animationen.js (Startseite) und hubs.js.
   Stellt window.BW bereit, damit die anderen Skripte dieselben Easings und
   die Bewegungsreduktion mitbenutzen.
   ========================================================================== */

window.BW = (function () {
  'use strict';

  // Ohne GSAP (Datei nicht geladen, etwa blockiert) bricht hier nichts ab:
  // BW meldet dann «weniger Bewegung», alle Skripte nehmen den ruhigen Weg,
  // den es für «Bewegung reduzieren» ohnehin gibt, und die Seite bleibt ganz
  // bedienbar, nur ohne Animation (seit 12.10.2026).
  const mitGsap = !!(window.gsap && window.ScrollTrigger && window.SplitText && window.Flip && window.CustomEase);
  if (mitGsap) {
    gsap.registerPlugin(ScrollTrigger, SplitText, Flip, CustomEase);
    CustomEase.create('bw-aus', '0.16, 1, 0.3, 1');
    CustomEase.create('bw-weich', '0.65, 0, 0.35, 1');
    gsap.defaults({ ease: 'bw-aus', duration: 1 });
  }

  const wenigerBewegung = !mitGsap || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     Tempo auf schwächeren Geräten (seit 14.10.2026)
     Gemessen auf einem nachgebildeten mittleren Android: Die Endlos-
     Schleifen (Sonne, Wellen, Fähren, Sterne, Dampf, Tropfen, Sticker)
     halten den Hauptthread fast ständig voll, weil jedes Bild die Ebenen
     der ganzen Seite neu ordnet. Drei Stufen:
       aus         alles wie bisher
       entlasten   Schleifen halten an, solange gescrollt wird und solange
                   sie nicht im Bild sind; sonst sieht alles aus wie bisher
       einfrieren  Schleifen stehen still. Was am Scrollen hängt (Fähren,
                   V60, Laufband, Parallax), bewegt sich nur beim Scrollen
     Welche Stufe gilt, steht in TEMPO, getrennt für Handys (Touch ohne
     Maus) und den Rest. Im Theme kommt sie aus den Theme-Einstellungen
     («Tempo», seit 0.26.17): Das Layout setzt window.BW_TEMPO, und das geht
     vor. Zum Ausprobieren: ?tempo=aus, ?tempo=entlasten oder
     ?tempo=einfrieren an eine Adresse hängen; der Browser merkt sich die
     Wahl, ?tempo=standard vergisst sie. «Bewegung reduzieren» geht vor.
     ------------------------------------------------------------------ */
  const STUFEN = ['aus', 'entlasten', 'einfrieren'];
  const TEMPO = { handy: 'aus', gross: 'aus' };
  const vomTheme = window.BW_TEMPO || {};
  ['handy', 'gross'].forEach((k) => { if (STUFEN.includes(vomTheme[k])) TEMPO[k] = vomTheme[k]; });
  const tempo = (function () {
    if (wenigerBewegung) return 'aus';
    let frage = null;
    try { frage = new URLSearchParams(location.search).get('tempo'); } catch (e) { /* ohne Adresse */ }
    let wahl = STUFEN.includes(frage) ? frage : null;
    try {
      if (frage === 'standard') localStorage.removeItem('bw-tempo');
      else if (wahl) localStorage.setItem('bw-tempo', wahl);
      else wahl = localStorage.getItem('bw-tempo');
    } catch (e) { /* ohne Speicher gilt die Wahl nur für diese Seite */ }
    if (STUFEN.includes(wahl)) return wahl;
    const handy = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    return handy ? TEMPO.handy : TEMPO.gross;
  })();
  if (tempo !== 'aus') document.documentElement.dataset.tempo = tempo;

  /* Nachlauf am Scroll (seit 19.10.2026, Befund Joscha am Pixel 6)
     scrub mit einer Zahl lässt ein Element die Scrollposition erst nach
     so vielen Sekunden einholen. Am Desktop wirkt das weich. Am Handy endet
     ein Wisch nach oben abrupt am Seitenanfang, danach rutschten Eyebrow
     und H1 noch nach, das sah aus wie ein Sprung. Auf Touch-Geräten folgen
     die Köpfe darum dem Finger ohne Nachlauf; Wege und Endlagen bleiben. */
  const aufTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  function scrub(wert) { return aufTouch ? true : wert; }

  /* Kein Intro am Handy (seit 22.10.2026, Vorschlag D, Entscheid Joscha):
     Auf Touch-Geräten ohne Maus stehen Hero und Seitenkopf vom ersten Bild
     an, ohne Vorhang, ohne Auftritt und ohne SplitText beim Laden. Das spart
     den Umbruch beim Öffnen des Vorhangs (PageSpeed: «Erzwungener
     dynamischer Umbruch», live rund 200 ms) und zeigt die Überschrift sofort
     (Speed Index). Am Computer bleibt alles wie bisher. Dieselbe Abfrage wie
     Tempo und der Vorhang in beanwatch.css. */
  const ohneIntro = aufTouch;

  /* Auftritt beim Laden (seit 20.10.2026, Befund Joscha am Pixel 6)
     Das Intro lief, während die Seite noch aufbaute: Fähren, Karten und
     Illustrationen hielten das Handy Hunderte Millisekunden besetzt, und
     die Schrift der H1 (Gabarito Black) kam oft erst danach, dann teilte
     SplitText die Überschrift mitten im Aufsteigen neu auf. bereit(fn)
     sammelt die Auftritte und startet sie, wenn die Seite geladen ist und
     ihre Schriften da sind (höchstens 500 ms Warten), zwei Bilder
     später, damit der erste schwere Aufbau schon gezeichnet ist. Im selben
     Bild geht der Vorhang auf (beanwatch.css, Ende), darum blitzt nichts. */
  const auftritte = [];
  let offen = false;
  function oeffnen() {
    if (offen) return;
    offen = true;
    // Während der Auftritte hält GSAP bei einem Ruckler kurz an, statt die
    // verpasste Zeit in einem Bild nachzuholen (gemessen am gedrosselten
    // Handy: ein Sprung der Eyebrow um 30 %). Danach wieder die Vorgabe.
    if (mitGsap && auftritte.length) {
      gsap.ticker.lagSmoothing(100, 16);
      setTimeout(() => gsap.ticker.lagSmoothing(500, 33), 2500);
    }
    auftritte.splice(0).forEach((fn) => {
      try { fn(); } catch (e) { console.error(e); }
    });
    document.documentElement.classList.add('bw-vorhang-offen');
  }
  function bereit(fn) {
    if (offen) fn(); else auftritte.push(fn);
  }
  const seiteGeladen = new Promise((fertig) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fertig, { once: true });
    else fertig();
  });
  // Die drei Schnitte der Köpfe gleich anfordern; nach dem Laden der Seite
  // zusätzlich auf alle Schnitte warten, die sie braucht (fonts.ready), sonst
  // teilt SplitText die Überschrift nochmals, wenn etwa der Schnitt der
  // Knöpfe nachkommt. Gewartet wird höchstens 500 ms nach dem Laden.
  const schnitte = document.fonts && document.fonts.load
    ? Promise.all(['400', '700', '900'].map((s) => document.fonts.load(s + ' 1em Gabarito'))).catch(() => {})
    : Promise.resolve();
  seiteGeladen
    .then(() => Promise.race([
      Promise.all([schnitte, document.fonts ? document.fonts.ready : null]),
      new Promise((fertig) => setTimeout(fertig, 500))
    ]))
    .then(() => requestAnimationFrame(() => requestAnimationFrame(oeffnen)));

  // Schleifen, die nie angehalten werden (etwa die Anleitung zum
  // Koffeinrechner, die der Leser selbst startet), und solche, die am
  // Scrollen hängen (Laufband).
  const ausnahmen = new WeakSet();
  const amScroll = new WeakSet();
  function tempoAusnahme(t) { if (t) ausnahmen.add(t); return t; }

  /* ------------------------------------------------------------------
     Header und Fortschrittsbalken
     ------------------------------------------------------------------ */
  function kopfleiste() {
    const kopf = document.getElementById('kopf');
    if (!kopf) return;

    if (!mitGsap) {
      const kleben = () => kopf.classList.toggle('ist-geklebt', window.scrollY > 80);
      window.addEventListener('scroll', kleben, { passive: true });
      kleben();
      return;
    }

    ScrollTrigger.create({
      start: 'top -80',
      end: 99999,
      onToggle: (self) => kopf.classList.toggle('ist-geklebt', self.isActive)
    });

    // Beim Direkteinstieg mit Anker sitzt der Scroll schon unten.
    if (window.scrollY > 80) kopf.classList.add('ist-geklebt');

    const balken = document.getElementById('fortschritt');
    if (balken) {
      gsap.to(balken, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.35 } });
    }
  }

  /* ------------------------------------------------------------------
     Sanftes Springen zu Ankerzielen
     Gilt für die ganze Seite, nicht nur für Inhaltsverzeichnisse: Auch die
     Sprünge von den Hubs zu #bohnen oder #rezeptur laufen hierüber.

     Warum nicht scroll-behavior:smooth im Stylesheet? Auf der Startseite
     hängt ein gepinnter ScrollTrigger an der Reise-Sektion. ScrollTrigger
     setzt die Scrollposition beim Pinnen und beim Refresh selbst und
     erwartet, dass das sofort passiert. Mit smooth im CSS würde auch das
     animiert. window.scrollTo animiert nur diesen einen Sprung.
     ------------------------------------------------------------------ */
  function ankersprung() {
    const abstand = () => {
      const wert = getComputedStyle(document.documentElement)
        .getPropertyValue('--ankerabstand');
      return parseInt(wert, 10) || 110;
    };

    // Bilder mit loading="lazy" bekommen erst Höhe, während man an ihnen
    // vorbeiscrollt, und schieben alles darunter nach. Das Ziel landet dann
    // ein paar Dutzend Pixel neben der berechneten Stelle. Nach dem Scrollen
    // deshalb einmal nachmessen — ausser der Leser hat selbst weitergescrollt.
    function nachmessen(ziel, sanft) {
      let abbruch = false;
      const stop = () => { abbruch = true; };
      const eigene = ['wheel', 'touchstart', 'keydown'];
      eigene.forEach(t => window.addEventListener(t, stop, { once: true, passive: true }));

      const pruefen = () => {
        eigene.forEach(t => window.removeEventListener(t, stop));
        if (abbruch) return;

        const weg = Math.round(ziel.getBoundingClientRect().top - abstand());
        // Am Seitenende lässt sich nicht weiter scrollen, da bleibt ein Rest.
        if (Math.abs(weg) < 5) return;
        window.scrollTo({ top: window.scrollY + weg, behavior: sanft ? 'smooth' : 'auto' });
      };

      if ('onscrollend' in window) {
        window.addEventListener('scrollend', pruefen, { once: true });
      } else {
        setTimeout(pruefen, 800);
      }
    }

    function hin(id, sanft) {
      const ziel = document.getElementById(id);
      if (!ziel) return false;

      const oben = ziel.getBoundingClientRect().top + window.scrollY - abstand();
      window.scrollTo({ top: oben, behavior: sanft ? 'smooth' : 'auto' });
      nachmessen(ziel, sanft);

      // Ohne Fokus liefe die Tab-Taste danach wieder oben in der Navigation
      // weiter statt im Abschnitt, zu dem man gerade gesprungen ist.
      ziel.setAttribute('tabindex', '-1');
      ziel.focus({ preventScroll: true });
      return true;
    }

    document.addEventListener('click', (e) => {
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const a = e.target.closest('a[href*="#"]');
      if (!a || a.target === '_blank') return;

      // Nur Ziele auf dieser Seite. /espresso.html#bohnen von anderswo bleibt
      // eine gewöhnliche Navigation.
      const url = new URL(a.href, location.href);
      if (url.pathname !== location.pathname || url.search !== location.search) return;

      const id = decodeURIComponent(url.hash.slice(1));
      if (!id) return;

      // #warenkorb, #abo und die Links im Footer sind noch Platzhalter ohne
      // Ziel. Die bleiben unangetastet, sonst wäre der Klick tot.
      if (!hin(id, !wenigerBewegung)) return;

      e.preventDefault();
      history.pushState(null, '', url.hash);
    });

    // Damit die Zurück-Taste nicht nur die Adresse ändert.
    window.addEventListener('popstate', () => {
      const id = decodeURIComponent(location.hash.slice(1));
      if (id) hin(id, !wenigerBewegung);
    });
  }

  /* ------------------------------------------------------------------
     Überschriften
     ------------------------------------------------------------------ */
  function ueberschriften() {
    // Am Handy bleiben die Köpfe oben ungeteilt (ohneIntro, oben).
    const teilen = (wahl) => gsap.utils.toArray(wahl).filter((el) => !(ohneIntro && el.closest('#hero, .seitenkopf')));
    teilen('[data-split="zeilen"]').forEach((el) => {
      SplitText.create(el, {
        type: 'lines',
        mask: 'lines',
        autoSplit: true,
        onSplit(self) {
          return gsap.from(self.lines, {
            yPercent: 112,
            duration: 1.05,
            stagger: 0.09,
            ease: 'bw-aus',
            scrollTrigger: { trigger: el, start: 'top 88%', once: true }
          });
        }
      });
    });

    teilen('[data-split="woerter"]').forEach((el) => {
      SplitText.create(el, {
        type: 'words,lines',
        mask: 'lines',
        autoSplit: true,
        onSplit(self) {
          return gsap.from(self.words, {
            opacity: 0.14,
            duration: 0.6,
            stagger: 0.035,
            ease: 'none',
            scrollTrigger: { trigger: el, start: 'top 82%', end: 'bottom 62%', scrub: 0.5 }
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Reveals
     ------------------------------------------------------------------ */
  function reveals() {
    /* Sicherheitsnetz (seit 14.10.2026). Die Auftritte lösen über
       ScrollTrigger aus, und dessen Positionen stammen vom Laden. Rutscht
       der Inhalt darüber danach nach oben, etwa am Handy, steht eine Box im
       Bild, bevor ihr Auftritt auslöst, und bleibt leer (Fund Joscha auf dem
       Pixel 6 bei «Welcher Kaffee passt zu mir?»). Darum zeigt das Ende
       jedes Scrollens alles, was dann im Bild steht und noch wartet. */
    const wartend = new Map();                 // Element -> zeigen(Elemente)
    const imBild = (el) => el.getBoundingClientRect().top < window.innerHeight * 0.95;
    function nachholen() {
      const faellig = new Map();
      wartend.forEach((zeigen, el) => {
        if (!imBild(el)) return;
        if (!faellig.has(zeigen)) faellig.set(zeigen, []);
        faellig.get(zeigen).push(el);
      });
      faellig.forEach((elemente, zeigen) => zeigen(elemente));
    }

    gsap.utils.toArray('[data-batch]').forEach((gruppe) => {
      const kinder = gsap.utils.toArray(':scope > *', gruppe);
      gsap.set(kinder, { opacity: 0, y: 46 });

      // Nur, was noch wartet: Hat das Sicherheitsnetz eine Box schon
      // gezeigt, startet ihr später auslösender Auftritt nicht noch einmal.
      const zeigen = (elemente) => {
        const neu = elemente.filter((el) => wartend.delete(el));
        if (neu.length) {
          gsap.to(neu, { opacity: 1, y: 0, duration: 1.05, stagger: 0.1, ease: 'bw-aus', overwrite: true });
        }
      };
      kinder.forEach((el) => wartend.set(el, zeigen));

      ScrollTrigger.batch(kinder, {
        start: 'top 88%',
        once: true,
        onEnter: zeigen
      });
    });

    gsap.utils.toArray('[data-reveal]').forEach((el) => {
      // Im Seitenkopf ist die Startbedingung beim Laden schon erfüllt. Dort
      // läuft das Bild deshalb mit der Überschrift zusammen statt als
      // eigener Scroll-Effekt — sonst starten beide unkoordiniert.
      const imKopf = el.closest('.beitragskopf, .seitenkopf');

      if (imKopf) {
        gsap.from(el, { opacity: 0, y: 30, duration: 1.1, delay: 0.25 });
        return;
      }

      const auftritt = gsap.from(el, {
        opacity: 0, y: 44, duration: 1.05,
        scrollTrigger: { trigger: el, start: 'top 88%', once: true, onEnter: () => wartend.delete(el) }
      });
      wartend.set(el, () => { if (wartend.delete(el)) auftritt.play(); });
    });

    ScrollTrigger.addEventListener('scrollEnd', nachholen);
  }

  /* ------------------------------------------------------------------
     Zahlen hochzählen
     ------------------------------------------------------------------ */
  function zaehler() {
    gsap.utils.toArray('[data-zaehler]').forEach((el) => {
      const ziel = parseFloat(el.dataset.zaehler);
      const dezimal = parseInt(el.dataset.dezimal || '0', 10);
      const suffix = el.dataset.suffix || '';
      const stand = { wert: 0 };

      gsap.to(stand, {
        wert: ziel,
        duration: 1.9,
        ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 88%', once: true },
        onUpdate() {
          const zahl = stand.wert.toFixed(dezimal).replace('.', ',');
          el.innerHTML = zahl + (suffix ? `<span class="einheit">${suffix}</span>` : '');
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Sonne und Wellen, die Signatur aus dem Hero
     ------------------------------------------------------------------ */
  /* Eingefroren (Tempo, oben) stehen Sonne und Wellen vom ersten Bild an
     still (seit 22.10.2026, Befund Joscha am Handy). Vorher liefen sie an,
     bis tempoFuehren sie fand, und sprangen dann in eine Zufallslage; auf der
     Startseite drehten sich die Strahlen beim Laden ein Stück und blieben
     stehen. Eine angehaltene Schleife sieht hier nicht anders aus als keine. */
  function sonne(auswahl) {
    if (tempo === 'einfrieren') return;
    const el = typeof auswahl === 'string' ? document.querySelector(auswahl) : auswahl;
    if (!el) return;
    const strahlen = el.querySelector('[data-strahlen]') || el.querySelector('g');
    const kern = el.querySelector('[data-kern]') || el.querySelector('circle');

    if (strahlen) {
      gsap.to(strahlen, { rotate: 360, duration: 90, repeat: -1, ease: 'none', transformOrigin: '50% 50%' });
    }
    if (kern) {
      gsap.to(kern, {
        scale: 1.09, opacity: 0.28, duration: 3.4,
        repeat: -1, yoyo: true, ease: 'sine.inOut', transformOrigin: '50% 50%'
      });
    }
  }

  /**
   * Endlos laufende Wellen. Jedes SVG ist doppelt so breit wie sein
   * Container und wandert um genau die halbe Breite, dadurch ist die
   * Schleife nahtlos.
   */
  function wellen(auswahl, tempi) {
    // Eingefroren stehen die Wellen still (siehe sonne, oben).
    if (tempo === 'einfrieren') return;
    const behaelter = typeof auswahl === 'string' ? document.querySelector(auswahl) : auswahl;
    if (!behaelter) return;
    /* :scope > svg, nicht einfach svg. Seit im Filterkaffee-Kopf eine Figur
       zwischen den Wellen steht, liegt deren Zeichnung im selben Behaelter.
       Ohne :scope fand die Suche sie mit, der Arm bekam den dritten Tempowert
       als linearen Schub um eine halbe Bildbreite und sprang alle 12 Sekunden
       zurueck. Die vorderste Welle rutschte dabei auf den Rueckfallwert 20 und
       lief zu langsam. Belebt werden nur die direkten Kinder des Bands. */
    const bahnen = behaelter.querySelectorAll(':scope > svg');
    const dauern = tempi || [26, 18, 12];

    bahnen.forEach((bahn, i) => {
      gsap.fromTo(bahn, { xPercent: 0 }, {
        xPercent: -50, duration: dauern[i] || 20, repeat: -1, ease: 'none'
      });
      gsap.to(bahn, {
        y: gsap.utils.random(-9, -4),
        duration: gsap.utils.random(3, 5),
        repeat: -1, yoyo: true, ease: 'sine.inOut'
      });
    });
  }

  /* ------------------------------------------------------------------
     Laufband, angetrieben von der Scroll-Geschwindigkeit
     ------------------------------------------------------------------ */
  function laufband(auswahl) {
    const spur = typeof auswahl === 'string' ? document.querySelector(auswahl) : auswahl;
    if (!spur) return;
    // Auf Touch-Geräten steht das Band still und zeigt nur den ersten Satz
    // (CSS am Ende von beanwatch.css, Wunsch Joscha 14.10.2026): Am Handy
    // war es kaum zu lesen und stockte beim Scrollen.
    if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) return;

    const band = gsap.to(spur, { xPercent: -50, duration: 26, repeat: -1, ease: 'none' });
    amScroll.add(band);
    // Eingefroren steht das Band, bis jemand scrollt.
    const ruhe = tempo === 'einfrieren' ? 0 : 1;
    band.timeScale(ruhe);

    ScrollTrigger.create({
      start: 0,
      end: 'max',
      onUpdate(self) {
        const v = self.getVelocity();
        const richtung = v < 0 ? -1 : 1;
        const schub = gsap.utils.clamp(1, 7, 1 + Math.abs(v) / 480);
        gsap.to(band, { timeScale: richtung * schub, duration: 0.3, overwrite: true });
        gsap.to(band, { timeScale: richtung * ruhe, duration: 1.1, delay: 0.32, overwrite: false });
      }
    });
  }

  /* ------------------------------------------------------------------
     Schleifen nach der Tempo-Stufe führen (siehe oben bei TEMPO)
     Gefunden werden alle Endlos-Schleifen auf der obersten Ebene von GSAP,
     deren Ziel ein Element der Seite ist, auch solche, die andere Skripte
     später anlegen: gesucht wird kurz nach dem Start, nach dem Laden und
     nach jedem Scrollen. Was ein Skript selbst angehalten hat, bleibt so.
     ------------------------------------------------------------------ */
  function tempoFuehren() {
    if (tempo === 'aus') return;
    const bekannt = new Map();          // Schleife -> Kasten, in dem sie läuft
    const angehalten = new WeakSet();   // von hier angehalten
    const sichtbar = new Map();         // Kasten -> im Bild?
    let scrollt = false;
    let uhr = 0;

    function kastenVon(t) {
      let ziel = t.targets ? t.targets()[0] : null;
      if (!ziel && t.getChildren) {
        const kind = t.getChildren(true, true, false).find((k) => k.targets);
        ziel = kind ? kind.targets()[0] : null;
      }
      if (!ziel || !ziel.closest) return null;
      return ziel.closest('section, header, footer, [data-laufband]') || ziel;
    }

    function anwenden() {
      bekannt.forEach((kasten, t) => {
        if (!t.parent) { bekannt.delete(t); return; }
        const laufen = sichtbar.get(kasten) !== false && (!scrollt || amScroll.has(t));
        if (laufen) {
          if (angehalten.has(t)) { angehalten.delete(t); t.resume(); }
        } else if (!t.paused()) {
          angehalten.add(t);
          t.pause();
        }
      });
    }

    const beobachter = tempo === 'entlasten'
      ? new IntersectionObserver((eintraege) => {
          eintraege.forEach((e) => sichtbar.set(e.target, e.isIntersecting));
          anwenden();
        }, { rootMargin: '80px 0px' })
      : null;

    function suchen() {
      gsap.globalTimeline.getChildren(false, true, true).forEach((t) => {
        if (t.repeat() !== -1 || bekannt.has(t) || ausnahmen.has(t)) return;
        const kasten = kastenVon(t);
        if (!kasten) return;
        if (tempo === 'einfrieren') {
          if (amScroll.has(t) || t.paused()) return;
          // Eben erst angelegt, stünde alles in der Anfangslage (Dampf
          // unsichtbar, alle Sterne gleich). Dann eine zufällige Lage.
          if (t.totalTime() < 0.5) t.progress(0.2 + Math.random() * 0.6);
          t.pause();
          bekannt.set(t, kasten);
          return;
        }
        bekannt.set(t, kasten);
        if (!sichtbar.has(kasten)) { sichtbar.set(kasten, true); beobachter.observe(kasten); }
      });
      if (tempo === 'entlasten') anwenden();
    }

    const nachher = () => {
      clearTimeout(uhr);
      uhr = setTimeout(() => { scrollt = false; suchen(); }, 220);
    };
    const scrollen = () => {
      if (tempo === 'entlasten' && !scrollt) { scrollt = true; anwenden(); }
      nachher();
    };
    window.addEventListener('scroll', scrollen, { passive: true });
    if (tempo === 'entlasten') window.addEventListener('touchmove', scrollen, { passive: true });

    setTimeout(suchen, 0);
    const geladen = () => { suchen(); setTimeout(suchen, 1500); setTimeout(suchen, 4000); };
    if (document.readyState === 'complete') geladen();
    else window.addEventListener('load', geladen);
  }

  /* ------------------------------------------------------------------
     Pfeile fuer eine waagrechte Spur
     Zwei Baender teilen sich das: die Kaffee-Reihe auf der Startseite und
     das Schritteband auf dem Espresso-Hub. Gescrollt wird nativ, die
     Pfeile springen nur um ganze Karten weiter und schalten sich an den
     Enden ab. Die Spur selbst bleibt bedienbar, auch wenn es gar keine
     Pfeile gibt: Sie ist im Markup fokussierbar und erbt damit die
     Pfeiltasten des Browsers.
     ------------------------------------------------------------------ */
  function spurPfeile(spur, pfeilLeiste, optionen) {
    if (!spur) return null;
    const o = optionen || {};
    const pfeile = pfeilLeiste ? Array.from(pfeilLeiste.querySelectorAll('.reihe-knopf')) : [];
    // erste(): welche Karte die Schrittweite vorgibt. Die Kaffee-Reihe
    // filtert, dort ist das die erste noch sichtbare. Sonst das erste Kind.
    const erste = o.erste || (() => spur.firstElementChild);

    // Breite einer Karte samt Abstand. Die Karten rasten links an der
    // Innenkante der Reihe ein, deshalb sitzt Karte i bei i mal dieser Breite.
    function kartenschritt() {
      const karte = erste();
      if (!karte) return spur.clientWidth;
      const abstand = parseFloat(getComputedStyle(spur).columnGap) || 0;
      return karte.offsetWidth + abstand;
    }

    function nachfuehren() {
      if (!pfeilLeiste) return;
      const ende = spur.scrollWidth - spur.clientWidth;
      pfeilLeiste.hidden = ende <= 2;
      pfeile.forEach((pfeil) => {
        const zurueck = Number(pfeil.dataset.richtung) < 0;
        pfeil.disabled = zurueck ? spur.scrollLeft <= 2 : spur.scrollLeft >= ende - 2;
      });
    }

    pfeile.forEach((pfeil) => {
      pfeil.addEventListener('click', () => {
        const schritt = kartenschritt();
        const innen = spur.clientWidth - parseFloat(getComputedStyle(spur).paddingLeft);
        const ganze = Math.max(1, Math.floor(innen / schritt));
        const jetzt = Math.round(spur.scrollLeft / schritt);
        const ziel = (jetzt + Number(pfeil.dataset.richtung) * ganze) * schritt;
        spur.scrollTo({ left: Math.max(0, ziel), behavior: wenigerBewegung ? 'auto' : 'smooth' });
      });
    });

    /* Ziehen mit gedrueckter Maustaste.
       Trackpad und Touch wischen laengst von selbst, eine Maus mit nur
       vertikalem Rad kommt aber nur ueber die Pfeile weiter. Diese Geste
       schliesst die Luecke. Drei Dinge machen sie vertraeglich:
       die Schwelle, damit ein Klick ein Klick bleibt; das Verschlucken des
       folgenden Klicks, damit das Loslassen ueber einer Karte nicht ihren
       Link oeffnet; und das Aussetzen der Rastung, die sonst bei jeder
       Zwischenposition einhakt und das Ziehen ruckeln laesst. */
    const SCHWELLE = 6;
    let zieht = false, gezogen = false, startX = 0, startScroll = 0, rastungAn = 0;

    function klickSchlucken(e) {
      e.stopPropagation();
      e.preventDefault();
    }

    spur.addEventListener('pointerdown', (e) => {
      // Nur die linke Maustaste. Touch und Stift wischen nativ, ein eigener
      // Handler wuerde ihnen die Traegheit nehmen.
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      zieht = true;
      gezogen = false;
      startX = e.clientX;
      startScroll = spur.scrollLeft;
    });

    spur.addEventListener('pointermove', (e) => {
      if (!zieht) return;
      const weg = e.clientX - startX;
      if (!gezogen) {
        if (Math.abs(weg) < SCHWELLE) return;
        gezogen = true;
        spur.setPointerCapture(e.pointerId);
        clearTimeout(rastungAn);
        spur.style.scrollSnapType = 'none';
        spur.style.userSelect = 'none';
        spur.style.cursor = 'grabbing';
      }
      spur.scrollLeft = startScroll - weg;
    });

    function loslassen(e) {
      if (!zieht) return;
      zieht = false;
      if (!gezogen) return;
      gezogen = false;
      if (e.pointerId !== undefined && spur.hasPointerCapture(e.pointerId)) {
        spur.releasePointerCapture(e.pointerId);
      }
      // Die Rastung kommt sofort zurueck. Wo sie im CSS steht, sucht sich der
      // Browser danach selbst die naechste Kante; wo nicht, bleibt das Band
      // stehen, wo man losgelassen hat.
      // Ein eigenes Ausrichten stand hier zwischenzeitlich und ist wieder
      // raus: Zusammen mit dem Aus- und Einschalten der Rastung brauchte es
      // gemessene 1000 ms, bis das Band zur Ruhe kam.
      spur.style.scrollSnapType = '';
      spur.style.userSelect = '';
      spur.style.cursor = '';
      // Der Klick kommt erst nach pointerup. Einmal abfangen, dann abmelden.
      spur.addEventListener('click', klickSchlucken, { capture: true, once: true });
      // Falls gar kein Klick folgt, weil der Zeiger ausserhalb losgelassen
      // wurde, raeumt dieser Nachlauf den Horcher wieder weg.
      setTimeout(() => spur.removeEventListener('click', klickSchlucken, true), 0);
    }

    spur.addEventListener('pointerup', loslassen);
    spur.addEventListener('pointercancel', loslassen);

    spur.addEventListener('scroll', nachfuehren, { passive: true });
    window.addEventListener('resize', nachfuehren);
    nachfuehren();
    return { nachfuehren };
  }

  /* ------------------------------------------------------------------
     Feinschliff
     ------------------------------------------------------------------ */
  function feinschliff() {
    gsap.utils.toArray('.produkt-bild img, .rezept-bild img, .wissen-bild img, .beitrag-bild img, .brewguide-bild--foto img')
      .forEach((bild) => {
        gsap.fromTo(bild, { scale: 1.16 }, {
          scale: 1, ease: 'none',
          scrollTrigger: { trigger: bild, start: 'top bottom', end: 'bottom top', scrub: 1 }
        });
      });

    gsap.utils.toArray('.produkt, .rezept, .wissen-karte, .beitrag, .einstieg, .brewguide, .tool-karte')
      .forEach((karte) => {
        const hoch = gsap.quickTo(karte, 'y', { duration: 0.45, ease: 'bw-aus' });
        karte.addEventListener('mouseenter', () => hoch(-8));
        karte.addEventListener('mouseleave', () => hoch(0));
      });

    gsap.utils.toArray('.btn, .reverse-schalter, .produkt-kaufen, .filter-knopf')
      .forEach((btn) => {
        const x = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'bw-aus' });
        const y = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'bw-aus' });
        btn.addEventListener('mousemove', (e) => {
          const feld = btn.getBoundingClientRect();
          x((e.clientX - feld.left - feld.width / 2) * 0.32);
          y((e.clientY - feld.top - feld.height / 2) * 0.42);
        });
        btn.addEventListener('mouseleave', () => { x(0); y(0); });
      });

    gsap.utils.toArray('.fuss-oben').forEach((fuss) => {
      gsap.from(fuss.children, {
        opacity: 0, y: 34, duration: 0.9, stagger: 0.09,
        scrollTrigger: { trigger: fuss, start: 'top 92%', once: true }
      });
    });
  }

  /* ------------------------------------------------------------------
     Seitenkopf einer Unterseite
     ------------------------------------------------------------------ */
  function seitenkopf() {
    const kopf = document.querySelector('.seitenkopf');
    if (!kopf) return;

    // Der Auftritt wartet, bis die Seite bereit ist (bereit, oben). Der Lead
    // steht vom ersten Bild an, nach ihm misst Google den Seitenaufbau (LCP,
    // Entscheid Joscha 20.10.2026). Am Handy gibt es keinen Auftritt
    // (ohneIntro, oben).
    if (!ohneIntro) bereit(() => {
      const tl = gsap.timeline({ delay: 0.1 });
      const krumen = kopf.querySelector('.brotkrumen');
      const eyebrow = kopf.querySelector('.eyebrow');
      const aktionen = kopf.querySelector('.kopf-aktionen-reihe');

      if (krumen) tl.from(krumen, { opacity: 0, y: 14, duration: 0.7 }, 0);
      if (eyebrow) tl.from(eyebrow, { opacity: 0, y: 16, duration: 0.7 }, 0.08);
      if (aktionen) tl.from(aktionen.children, { opacity: 0, y: 20, duration: 0.8, stagger: 0.08 }, 0.62);
    });

    // Der Kopf schiebt sich beim Weiterscrollen weg.
    gsap.to(kopf.querySelector('.wrap'), {
      yPercent: -14, opacity: 0.25, ease: 'none',
      scrollTrigger: { trigger: kopf, start: 'top top', end: 'bottom top', scrub: scrub(0.6) }
    });
  }

  /* ------------------------------------------------------------------
     Start
     ------------------------------------------------------------------ */
  function start() {
    ankersprung();

    if (wenigerBewegung) {
      // Ohne Auftritte gibt es nichts zu warten: Vorhang sofort auf.
      oeffnen();
      if (mitGsap) gsap.set('[data-reveal], [data-batch] > *', { opacity: 1, y: 0 });
      // Zahlen trotzdem korrekt anzeigen, nur ohne Zählanimation.
      document.querySelectorAll('[data-zaehler]').forEach((el) => {
        const dez = parseInt(el.dataset.dezimal || '0', 10);
        const zahl = parseFloat(el.dataset.zaehler).toFixed(dez).replace('.', ',');
        el.innerHTML = zahl + (el.dataset.suffix ? `<span class="einheit">${el.dataset.suffix}</span>` : '');
      });
      kopfleiste();
      return;
    }

    kopfleiste();
    // Erst teilen, wenn die Schrift da ist, sonst teilt SplitText neu auf.
    bereit(ueberschriften);
    reveals();
    zaehler();
    feinschliff();
    seitenkopf();
    sonne('[data-sonne]');
    wellen('[data-wellen]', [26, 18, 12]);
    laufband('[data-laufband]');
    tempoFuehren();

    window.addEventListener('load', () => ScrollTrigger.refresh());
  }

  // Erst laufen lassen, wenn die Shell im DOM steht.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Ohne GSAP tun die Animationen nichts; spurPfeile braucht kein GSAP.
  const nichts = () => null;
  return mitGsap
    ? { wenigerBewegung, mitGsap, tempo, tempoAusnahme, scrub, ohneIntro, bereit, sonne, wellen, laufband, seitenkopf, spurPfeile }
    : { wenigerBewegung, mitGsap, tempo, tempoAusnahme, scrub, ohneIntro, bereit, sonne: nichts, wellen: nichts, laufband: nichts, seitenkopf: nichts, spurPfeile };
})();
